import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Check, X, ChevronUp, ChevronDown, ChevronsUpDown, Search, ExternalLink, Info, Loader2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FEATURE_KEYS = [
  'webspace', 'domains', 'vserver', 'dedi',
  'discordbots', 'musicbots', 'licenses', 'teamspeak', 'gameserver',
];

const MOCK_HOSTINGS = [
  { id: 1, name: "Conrise", webspace: 1, domains: 1, vserver: 1, dedi: 0, discordbots: 0, musicbots: 0, licenses: 0, teamspeak: 0, gameserver: 0, url: "https://conrise.de/" },
  { id: 2, name: "dashserv", webspace: 1, domains: 1, vserver: 1, dedi: 1, discordbots: 1, musicbots: 1, licenses: 0, teamspeak: 1, gameserver: 1, url: "https://dashserv.io/" },
  { id: 3, name: "Deinserverhost", webspace: 1, domains: 1, vserver: 1, dedi: 1, discordbots: 1, musicbots: 1, licenses: 1, teamspeak: 1, gameserver: 1, url: "https://deinserverhost.de/" },
  { id: 4, name: "Devstorage", webspace: 1, domains: 1, vserver: 1, dedi: 1, discordbots: 1, musicbots: 1, licenses: 1, teamspeak: 1, gameserver: 1, url: "https://devstorage.eu/" },
  { id: 5, name: "ServerQuote", webspace: 1, domains: 1, vserver: 1, dedi: 1, discordbots: 1, musicbots: 1, licenses: 1, teamspeak: 1, gameserver: 1, url: "https://squote.de/" },
  { id: 6, name: "Gameserver.gratis", webspace: 0, domains: 0, vserver: 0, dedi: 0, discordbots: 1, musicbots: 0, licenses: 0, teamspeak: 0, gameserver: 0, url: "https://gameserver.gratis/" },
  { id: 7, name: "Hosmatic", webspace: 1, domains: 1, vserver: 1, dedi: 1, discordbots: 1, musicbots: 1, licenses: 1, teamspeak: 1, gameserver: 1, url: "https://www.hosmatic.com/" },
  { id: 8, name: "INSTANTHOST", webspace: 1, domains: 1, vserver: 1, dedi: 1, discordbots: 0, musicbots: 1, licenses: 1, teamspeak: 1, gameserver: 1, url: "https://instanthost.gg/" },
  { id: 9, name: "Mine-Hoster", webspace: 0, domains: 1, vserver: 0, dedi: 0, discordbots: 0, musicbots: 0, licenses: 0, teamspeak: 0, gameserver: 1, url: "https://www.mine-hoster.de/" },
  { id: 10, name: "Necror", webspace: 1, domains: 1, vserver: 1, dedi: 1, discordbots: 0, musicbots: 0, licenses: 0, teamspeak: 1, gameserver: 1, url: "https://necror.de/" },
  { id: 11, name: "OpusX", webspace: 1, domains: 1, vserver: 1, dedi: 1, discordbots: 0, musicbots: 0, licenses: 1, teamspeak: 0, gameserver: 0, url: "https://www.opusx.io/" },
  { id: 12, name: "Prepaid-Host", webspace: 1, domains: 1, vserver: 1, dedi: 0, discordbots: 0, musicbots: 0, licenses: 0, teamspeak: 1, gameserver: 1, url: "https://prepaid-host.com/" },
  { id: 13, name: "SpexMC", webspace: 1, domains: 1, vserver: 1, dedi: 0, discordbots: 0, musicbots: 0, licenses: 0, teamspeak: 0, gameserver: 1, url: "https://spexmc.eu/" },
  { id: 14, name: "ST Hosting", webspace: 1, domains: 1, vserver: 1, dedi: 1, discordbots: 0, musicbots: 0, licenses: 1, teamspeak: 1, gameserver: 1, url: "https://signaltransmitter.de/" },
  { id: 15, name: "Emeraldhost", webspace: 0, domains: 1, vserver: 1, dedi: 0, discordbots: 0, musicbots: 0, licenses: 0, teamspeak: 1, gameserver: 1, url: "https://emeraldhost.de/" },
  { id: 16, name: "dNodes", webspace: 1, domains: 1, vserver: 1, dedi: 1, discordbots: 1, musicbots: 1, licenses: 1, teamspeak: 1, gameserver: 1, url: "https://dnodes.net/" },
  { id: 17, name: "HeroServ", webspace: 1, domains: 1, vserver: 1, dedi: 0, discordbots: 1, musicbots: 0, licenses: 1, teamspeak: 1, gameserver: 0, url: "https://heroserv.io/" },
  { id: 18, name: "Pfcloud", webspace: 1, domains: 1, vserver: 1, dedi: 1, discordbots: 0, musicbots: 0, licenses: 0, teamspeak: 0, gameserver: 0, url: "https://pfcloud.io/" },
  { id: 19, name: "SRV.RENT UG", webspace: 1, domains: 1, vserver: 1, dedi: 1, discordbots: 0, musicbots: 0, licenses: 0, teamspeak: 0, gameserver: 1, url: "https://www.srv.rent/" },
  { id: 20, name: "Host-Zentrum", webspace: 1, domains: 1, vserver: 1, dedi: 1, discordbots: 0, musicbots: 0, licenses: 0, teamspeak: 0, gameserver: 1, url: "https://host-zentrum.de/" },
];

function SortIcon({ col, sortConfig }) {
  if (sortConfig.key !== col) return <ChevronsUpDown className="w-3.5 h-3.5 text-slate-600" />;
  return sortConfig.direction === 'asc'
    ? <ChevronUp className="w-3.5 h-3.5 text-cyan-400" />
    : <ChevronDown className="w-3.5 h-3.5 text-cyan-400" />;
}

function FeatureCell({ value }) {
  return value === 1
    ? <Check className="w-4 h-4 text-green-400 mx-auto" />
    : <X className="w-4 h-4 text-slate-700 mx-auto" />;
}

export function HostingTable() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [hostings, setHostings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });

  useEffect(() => {
    const fetchHostings = async () => {
      try {
        const res = await axios.get(`${API}/hostings`, { timeout: 8000 });
        const data = Array.isArray(res.data) ? res.data : [];
        setHostings(data.length > 0 ? data : MOCK_HOSTINGS);
      } catch {
        setHostings(MOCK_HOSTINGS);
      } finally {
        setLoading(false);
      }
    };
    fetchHostings();
  }, []);

  const toggleFilter = (key) => {
    setActiveFilters((prev) =>
      prev.includes(key) ? prev.filter((f) => f !== key) : [...prev, key]
    );
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const filtered = useMemo(() => {
    let result = hostings.filter((h) => {
      const nameMatch = h.name.toLowerCase().includes(searchQuery.toLowerCase());
      const featureMatch = activeFilters.every((f) => h[f] === 1);
      return nameMatch && featureMatch;
    });

    return [...result].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];
      if (typeof aVal === 'string') {
        return sortConfig.direction === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }, [hostings, searchQuery, activeFilters, sortConfig]);

  return (
    <section
      id="vergleich"
      className="py-20 bg-[#020817] relative"
      data-testid="hosting-table-section"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section header */}
        <div className="mb-10">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-2">
            {t.table.title}
          </h2>
          <p className="text-sm text-slate-500">
            {t.table.count(filtered.length, hostings.length)}
          </p>
        </div>

        {/* Search + Filters */}
        <div className="mb-6 space-y-4">
          {/* Search bar */}
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t.table.searchPlaceholder}
              data-testid="search-input"
              className="w-full pl-9 pr-4 py-2.5 bg-[#0f172a] border border-slate-700 rounded-lg text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
            />
          </div>

          {/* Feature filter chips */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-slate-500 uppercase tracking-wider mr-1">
              {t.table.filterLabel}
            </span>
            {FEATURE_KEYS.map((key) => (
              <button
                key={key}
                onClick={() => toggleFilter(key)}
                data-testid={`filter-chip-${key}`}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                  activeFilters.includes(key)
                    ? 'bg-cyan-500 border-cyan-500 text-slate-950'
                    : 'bg-transparent border-slate-700 text-slate-400 hover:border-slate-500 hover:text-slate-200'
                }`}
              >
                {t.features[key]}
              </button>
            ))}
            {activeFilters.length > 0 && (
              <button
                onClick={() => setActiveFilters([])}
                data-testid="clear-filters-btn"
                className="px-3 py-1 rounded-full text-xs font-medium text-slate-500 hover:text-white transition-colors"
              >
                {t.table.filterAll} ×
              </button>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="w-full overflow-x-auto rounded-xl border border-slate-800 bg-[#0f172a]/50 backdrop-blur-sm">
          {loading ? (
            <div className="flex items-center justify-center py-20 gap-3" data-testid="table-loading">
              <Loader2 className="w-5 h-5 text-cyan-400 animate-spin" />
              <span className="text-slate-400">{t.table.loading}</span>
            </div>
          ) : (
            <table className="w-full min-w-[900px]" data-testid="hosting-table">
              <thead>
                <tr className="border-b border-slate-800">
                  {/* Provider column */}
                  <th className="sticky left-0 bg-[#0f172a] z-10 px-3 py-3 text-left w-[110px]">
                    <button
                      onClick={() => handleSort('name')}
                      data-testid="sort-name"
                      className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-400 hover:text-white transition-colors"
                    >
                      {t.table.provider}
                      <SortIcon col="name" sortConfig={sortConfig} />
                    </button>
                  </th>

                  {/* Feature columns */}
                  {FEATURE_KEYS.map((key) => (
                    <th key={key} className="px-2 py-3 text-center">
                      <button
                        onClick={() => handleSort(key)}
                        data-testid={`sort-${key}`}
                        className={`flex items-center gap-1 mx-auto text-xs font-semibold uppercase tracking-wide transition-colors ${
                          sortConfig.key === key
                            ? 'text-cyan-400'
                            : 'text-slate-400 hover:text-white'
                        }`}
                      >
                        <span className="hidden lg:inline">{t.features[key]}</span>
                        <span className="lg:hidden">{t.features[key].slice(0, 3)}</span>
                        <SortIcon col={key} sortConfig={sortConfig} />
                      </button>
                    </th>
                  ))}

                  {/* Action column */}
                  <th className="px-4 py-3 text-right">
                    <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                      {t.table.action}
                    </span>
                  </th>
                </tr>
              </thead>

              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={FEATURE_KEYS.length + 2} className="py-16 text-center text-slate-500">
                      {t.table.noResults}
                    </td>
                  </tr>
                ) : (
                  filtered.map((hosting, idx) => (
                    <tr
                      key={hosting.id || idx}
                      data-testid={`hosting-row-${idx}`}
                      onClick={() => navigate(`/anbieter/${hosting.id}`)}
                      className="border-b border-slate-800/60 hover:bg-cyan-500/[0.04] transition-colors group cursor-pointer"
                    >
                      {/* Provider name + tooltip */}
                      <td className="sticky left-0 bg-[#0f172a] group-hover:bg-[#0d1f38] transition-colors px-3 py-3.5 z-10">
                        <div className="flex items-center gap-1.5 overflow-hidden">
                          <span
                            data-testid={`provider-name-${idx}`}
                            className="font-semibold text-white group-hover:text-cyan-400 transition-colors text-sm flex-1 min-w-0 truncate"
                          >
                            {hosting.name}
                          </span>
                          {/* Tooltip – hidden on mobile to save space */}
                          <span className="relative flex-shrink-0 hidden sm:inline-block">
                            <Info className="w-3.5 h-3.5 text-slate-600 group-hover:text-cyan-500 transition-colors" />
                            <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 -top-8 px-2 py-1 rounded-md bg-slate-800 border border-slate-700 text-xs text-slate-200 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-150 shadow-lg z-50">
                              {t.table.rowTooltip}
                            </span>
                          </span>
                        </div>
                      </td>

                      {/* Feature cells */}
                      {FEATURE_KEYS.map((key) => (
                        <td key={key} className="px-2 py-3.5 text-center">
                          <FeatureCell value={hosting[key]} />
                        </td>
                      ))}

                      {/* Visit button – stopPropagation so row click doesn't fire */}
                      <td className="px-4 py-3.5 text-right" onClick={e => e.stopPropagation()}>
                        <a
                          href={hosting.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`visit-btn-${idx}`}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 rounded-lg text-xs font-semibold hover:bg-cyan-500 hover:text-slate-950 hover:border-cyan-500 transition-colors"
                        >
                          {t.table.visit}
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Sort tip */}
        <p className="mt-3 text-xs text-slate-600 text-center">
          {t.table.sortTip}
        </p>
      </div>
    </section>
  );
}
